import React, { useState, useEffect } from 'react';
import { Search, Upload, Download, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { generateDemoInstruments } from './demoData';

const API_URL = '/api';

// ─── CSV helper: parse a single CSV line respecting quoted fields ───
// Handles commas inside quotes like: ASHOKLEY,"Ashok Leyland Limited","82,093.21",PVT
function parseCSVLine(line) {
  const values = [];
  let current = '';
  let insideQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      insideQuotes = !insideQuotes;
    } else if (ch === ',' && !insideQuotes) {
      values.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  values.push(current.trim()); // last field
  return values;
}

// ─── CSV helper: map any column header name to the database field name ───
// Recognizes many variations so the user doesn't have to match exact names
function mapHeaderToField(rawHeader) {
  const h = rawHeader.toLowerCase().replace(/[^a-z0-9]/g, ''); // strip all non-alphanumeric
  // Symbol
  if (h === 'symbol') return 'symbol';
  // Underlying
  if (h === 'underlying' || h === 'underlyingname' || h === 'companyname' || h === 'name') return 'underlying';
  // Market Cap
  if (h.includes('marketcap') || h.includes('mcap') || h === 'marketcapincr' || h === 'marketcapcr') return 'market_cap';
  // Public/Private
  if (h.includes('public') || h === 'publicprivate' || h === 'type') return 'public_private';
  // Sector
  if (h === 'sector' || h === 'industry') return 'sector';
  // Category
  if (h === 'category' || h === 'cat') return 'category';
  // Enabled
  if (h === 'enabled' || h === 'active' || h === 'status') return 'enabled';
  // Unrecognized — return null (will be skipped)
  return null;
}

// ─── Helper: parse market_cap string like "82,093.21" into a number ───
// Returns -1 for N/A or invalid so they sort to the bottom
function parseMarketCap(val) {
  if (!val || val === 'N/A') return -1;
  const num = parseFloat(String(val).replace(/,/g, ''));
  return isNaN(num) ? -1 : num;
}

const TradableUniverse = ({ demoMode = false }) => {
  const [instruments, setInstruments] = useState([]);
  const [filteredInstruments, setFilteredInstruments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    publicPrivate: 'All',
    sector: 'All',
    category: 'All',
    status: 'All'
  });
  const [mcapMin, setMcapMin] = useState('');
  const [mcapMax, setMcapMax] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [lastSync, setLastSync] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [bulkToggling, setBulkToggling] = useState(false);

  // Fetch instruments from backend (or load demo data)
  useEffect(() => {
    if (demoMode) {
      setInstruments(generateDemoInstruments());
      setLastSync('Demo Mode');
      setLoading(false);
    } else {
      fetchInstruments();
      fetchSyncStatus();
    }
  }, [demoMode]);

  const fetchInstruments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/instruments`);
      setInstruments(response.data);
    } catch (error) {
      console.error('Error fetching instruments:', error);
      alert('Failed to fetch instruments. Please check if backend server is running.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/sync/status`);
      if (response.data.last_sync) {
        setLastSync(new Date(response.data.last_sync).toLocaleString());
      }
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  const handleSyncNow = async () => {
    if (syncing) return;
    if (demoMode) { alert('Sync is not available in demo mode. Login to sync real instruments from NSE.'); return; }
    
    const confirmed = window.confirm('This will fetch the latest F&O instruments from NSE and update market caps. This may take 1-2 minutes. Continue?');
    if (!confirmed) return;

    try {
      setSyncing(true);
      let response = await axios.post(`${API_URL}/sync/nse`, { confirmShrink: false });

      // The new list is much shorter than the one we hold — nothing has been deleted yet.
      // Show the actual numbers and let the user decide before anything is removed.
      if (response.data.needsConfirmation) {
        const { incoming_count, current_count, shrink_pct } = response.data;
        const proceed = window.confirm(
          `Are you sure?\n\n` +
          `The new list from NSE has only ${incoming_count} stocks, but your current list has ${current_count} ` +
          `— that is ${shrink_pct}% smaller.\n\n` +
          `Nothing has been deleted yet. New stocks in the list have already been added.\n\n` +
          `OK = replace the list anyway (${current_count - incoming_count} stocks will be removed).\n` +
          `Cancel = keep the current list and change nothing.`
        );
        if (!proceed) {
          alert('Cancelled. Your existing stock list is unchanged — nothing was removed.');
          await fetchInstruments();
          return;
        }
        response = await axios.post(`${API_URL}/sync/nse`, { confirmShrink: true });
      }

      alert(`Sync completed! ${response.data.instruments_synced} instruments synced.`);
      await fetchInstruments();
      await fetchSyncStatus();
    } catch (error) {
      console.error('Error syncing:', error);
      alert('Sync failed. Please check your internet connection and try again.');
    } finally {
      setSyncing(false);
    }
  };

  // Get unique values for filters
  const uniquePublicPrivate = ['All', ...new Set(instruments.map(i => i.public_private).filter(Boolean))];
  const uniqueSectors = ['All', ...new Set(instruments.map(i => i.sector).filter(Boolean))];
  const uniqueCategories = ['All', 'A', 'B', 'C', 'D', 'Blank'];

  // Filter and search logic (includes MCAP min/max)
  useEffect(() => {
    let filtered = instruments;

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(inst => 
        inst.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inst.underlying.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply filters
    if (filters.publicPrivate !== 'All') {
      filtered = filtered.filter(inst => inst.public_private === filters.publicPrivate);
    }
    if (filters.sector !== 'All') {
      filtered = filtered.filter(inst => inst.sector === filters.sector);
    }
    if (filters.category !== 'All') {
      if (filters.category === 'Blank') {
        filtered = filtered.filter(inst => !inst.category || inst.category === '');
      } else {
        filtered = filtered.filter(inst => inst.category === filters.category);
      }
    }
    if (filters.status !== 'All') {
      filtered = filtered.filter(inst => 
        filters.status === 'Enabled' ? inst.enabled : !inst.enabled
      );
    }

    // Apply MCAP min/max filter
    const minVal = mcapMin !== '' ? parseFloat(mcapMin) : null;
    const maxVal = mcapMax !== '' ? parseFloat(mcapMax) : null;
    if (minVal !== null || maxVal !== null) {
      filtered = filtered.filter(inst => {
        const mcap = parseMarketCap(inst.market_cap);
        if (mcap === -1) return false; // Exclude N/A when MCAP filter is active
        if (minVal !== null && mcap < minVal) return false;
        if (maxVal !== null && mcap > maxVal) return false;
        return true;
      });
    }

    setFilteredInstruments(filtered);
  }, [searchTerm, filters, instruments, mcapMin, mcapMax]);

  // Sort logic
  // For market_cap: converts text like "82,093.21" to a number before comparing
  // Items with "N/A" or empty market cap always go to the bottom
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredInstruments].sort((a, b) => {
      if (key === 'market_cap') {
        const aNum = parseMarketCap(a.market_cap);
        const bNum = parseMarketCap(b.market_cap);
        // N/A (value -1) always goes to the bottom regardless of sort direction
        if (aNum === -1 && bNum === -1) return 0;
        if (aNum === -1) return 1;
        if (bNum === -1) return -1;
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      }
      // Default: alphabetical/numeric comparison for all other columns
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setFilteredInstruments(sorted);
  };

  // Toggle instrument
  const toggleInstrument = async (id) => {
    // Update UI (in demo mode this is the only thing that happens — not saved)
    setInstruments(prev => prev.map(inst => 
      inst.id === id ? { ...inst, enabled: inst.enabled === 1 ? 0 : 1 } : inst
    ));
    
    if (demoMode) return; // Demo mode — don't call backend
    
    try {
      await axios.post(`${API_URL}/instruments/${id}/toggle`);
    } catch (error) {
      console.error('Error toggling instrument:', error);
      alert('Failed to toggle instrument. Please try again.');
      await fetchInstruments();
    }
  };

  // Bulk enable or disable all currently filtered instruments
  const handleBulkToggle = async (enable) => {
    if (demoMode) { alert('Bulk toggle is not available in demo mode.'); return; }
    if (filteredInstruments.length === 0) { alert('No instruments match current filters.'); return; }
    if (bulkToggling) return;

    const action = enable ? 'Enable' : 'Disable';
    const confirmed = window.confirm(
      `${action} ${filteredInstruments.length} filtered instruments?\n\nThis will ${action.toLowerCase()} all instruments currently shown in the table.`
    );
    if (!confirmed) return;

    try {
      setBulkToggling(true);
      const ids = filteredInstruments.map(inst => inst.id);
      const response = await axios.post(`${API_URL}/instruments/bulk-toggle`, {
        ids,
        enabled: enable ? 1 : 0
      });
      alert(`${action}d ${response.data.updated} instruments!`);
      await fetchInstruments();
    } catch (error) {
      console.error('Error bulk toggling:', error);
      alert(`Failed to ${action.toLowerCase()} instruments. Please try again.`);
    } finally {
      setBulkToggling(false);
    }
  };

  // Edit cell
  const handleCellEdit = async (id, field, value) => {
    // Update UI (in demo mode this is the only thing that happens — not saved)
    setInstruments(prev => prev.map(inst => 
      inst.id === id ? { ...inst, [field]: value } : inst
    ));
    setEditingCell(null);
    
    if (demoMode) return; // Demo mode — don't call backend
    
    try {
      await axios.put(`${API_URL}/instruments/${id}`, { [field]: value });
    } catch (error) {
      console.error('Error updating instrument:', error);
      alert('Failed to update. Please try again.');
      await fetchInstruments();
    }
  };

  // ─── Handle CSV file upload ───
  // Uses parseCSVLine() to handle commas inside quoted values (e.g., market cap "82,093.21")
  // Uses mapHeaderToField() to recognize various column name formats
  // Flexible: only sends fields that exist in the CSV — missing columns are left unchanged
  const handleFileUpload = (event) => {
    if (demoMode) { alert('CSV upload is not available in demo mode. Login to upload real data.'); event.target.value = ''; return; }
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          alert('File is empty or invalid format.');
          return;
        }

        // Parse header row and map to database field names
        const rawHeaders = parseCSVLine(lines[0]);
        const fieldMap = rawHeaders.map(h => mapHeaderToField(h));

        // Check that at least 'symbol' column exists
        if (!fieldMap.includes('symbol')) {
          alert('CSV must have a "Symbol" column. Could not find one in the headers:\n' + rawHeaders.join(', '));
          return;
        }

        // Parse data rows
        const parsedData = lines.slice(1).map(line => {
          const values = parseCSVLine(line);
          const obj = {};
          fieldMap.forEach((field, index) => {
            if (field && values[index] !== undefined) {
              obj[field] = values[index];
            }
          });
          return obj;
        }).filter(obj => obj.symbol && obj.symbol.trim() !== '');

        if (parsedData.length === 0) {
          alert('No valid data found in file.');
          return;
        }

        // Show which columns were recognized
        const recognized = fieldMap.filter(f => f !== null);
        const skipped = rawHeaders.filter((h, i) => fieldMap[i] === null);

        await axios.post(`${API_URL}/instruments/custom-data`, { customData: parsedData });
        
        let msg = `Successfully updated ${parsedData.length} instruments!\nColumns used: ${recognized.join(', ')}`;
        if (skipped.length > 0) {
          msg += `\nColumns skipped (not recognized): ${skipped.join(', ')}`;
        }
        alert(msg);
        await fetchInstruments();
      } catch (error) {
        console.error('Error importing:', error);
        alert('Import failed. Please check file format.\nRequired: Symbol column\nOptional: Market Cap, Public/Private, Sector, Category, Enabled');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  // ─── Download to CSV ───
  // Wraps values containing commas in quotes so the file can be re-uploaded correctly
  const handleDownload = () => {
    const headers = ['Symbol', 'Underlying', 'Market Cap (In Cr)', 'PUBLIC/Private', 'Sector', 'Category', 'Enabled'];

    // Helper: wrap value in quotes if it contains a comma
    const csvVal = (val) => {
      const s = String(val ?? '');
      return s.includes(',') ? `"${s}"` : s;
    };

    const csvContent = [
      headers.join(','),
      ...instruments.map(inst => [
        inst.symbol,
        csvVal(inst.underlying),
        csvVal(inst.market_cap || 'N/A'),
        inst.public_private || '',
        inst.sector || '',
        inst.category || '',
        inst.enabled ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tradable_universe_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Stats
  const totalInstruments = instruments.length;
  const enabledInstruments = instruments.filter(i => i.enabled).length;
  const stocksCount = instruments.filter(i => i.public_private !== 'Index').length;
  const indicesCount = instruments.filter(i => i.public_private === 'Index').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading instruments...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Tradable Universe</h1>
            <p className="text-sm text-gray-600 mt-1">Manage your F&O trading instruments</p>
            {demoMode && <span style={{ display: 'inline-block', marginTop: 4, padding: '2px 8px', fontSize: 10, fontWeight: 600, background: '#92400e', color: '#fbbf24', borderRadius: 3 }}>DEMO — changes are not saved</span>}
          </div>
          <div className="flex gap-3">
            <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
              <Upload size={18} />
              Upload CSV
              <input type="file" accept=".csv" onChange={handleFileUpload} className="hidden" />
            </label>
            <button onClick={handleDownload} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              <Download size={18} />
              Download
            </button>
            <button 
              onClick={handleSyncNow} 
              disabled={syncing}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                syncing 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <RefreshCw size={18} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="text-sm text-gray-600">Total Instruments</div>
            <div className="text-2xl font-bold text-gray-800">{totalInstruments}</div>
            <div className="text-xs text-gray-500 mt-1">{stocksCount} Stocks, {indicesCount} Indices</div>
          </div>
          <div className="bg-green-50 p-3 rounded-lg">
            <div className="text-sm text-gray-600">Enabled</div>
            <div className="text-2xl font-bold text-green-600">{enabledInstruments}</div>
            <div className="text-xs text-gray-500 mt-1">Active for trading</div>
          </div>
          <div className="bg-orange-50 p-3 rounded-lg">
            <div className="text-sm text-gray-600">Disabled</div>
            <div className="text-2xl font-bold text-orange-600">{totalInstruments - enabledInstruments}</div>
            <div className="text-xs text-gray-500 mt-1">Not available</div>
          </div>
          <div className="bg-purple-50 p-3 rounded-lg">
            <div className="text-sm text-gray-600">Last Sync</div>
            <div className="text-sm font-bold text-gray-800">{lastSync || 'Never'}</div>
            <div className="text-xs text-green-600 mt-1 flex items-center gap-1">
              {lastSync && <CheckCircle size={12} />}
              {lastSync ? 'Up to date' : 'Click Sync Now'}
            </div>
          </div>
        </div>

        {/* Search and Filters — Row 1 */}
        <div className="flex gap-3 items-center mb-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by symbol or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={filters.publicPrivate}
            onChange={(e) => setFilters({ ...filters, publicPrivate: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {uniquePublicPrivate.map(opt => (
              <option key={opt} value={opt}>{opt === 'All' ? 'All Types' : opt}</option>
            ))}
          </select>

          <select
            value={filters.sector}
            onChange={(e) => setFilters({ ...filters, sector: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {uniqueSectors.map(opt => (
              <option key={opt} value={opt}>{opt === 'All' ? 'All Sectors' : opt}</option>
            ))}
          </select>

          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {uniqueCategories.map(opt => (
              <option key={opt} value={opt}>{opt === 'All' ? 'All Categories' : opt}</option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="All">All Status</option>
            <option value="Enabled">Enabled</option>
            <option value="Disabled">Disabled</option>
          </select>
        </div>

        {/* Filters — Row 2: MCAP range + Bulk Enable/Disable */}
        <div className="flex gap-3 items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 font-medium whitespace-nowrap">MCap (Cr):</span>
            <input
              type="number"
              placeholder="Min"
              value={mcapMin}
              onChange={(e) => setMcapMin(e.target.value)}
              className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            <span className="text-gray-400">—</span>
            <input
              type="number"
              placeholder="Max"
              value={mcapMax}
              onChange={(e) => setMcapMax(e.target.value)}
              className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            {(mcapMin !== '' || mcapMax !== '') && (
              <button
                onClick={() => { setMcapMin(''); setMcapMax(''); }}
                className="text-xs text-gray-500 hover:text-red-500 underline"
              >
                Clear
              </button>
            )}
          </div>

          <div className="flex-1" />

          {/* Bulk Enable / Disable buttons */}
          <button
            onClick={() => handleBulkToggle(true)}
            disabled={bulkToggling || filteredInstruments.length === 0}
            className={`px-4 py-2 text-sm font-medium rounded-lg ${
              bulkToggling || filteredInstruments.length === 0
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-300'
            }`}
          >
            {bulkToggling ? 'Working...' : `Enable All Filtered (${filteredInstruments.length})`}
          </button>
          <button
            onClick={() => handleBulkToggle(false)}
            disabled={bulkToggling || filteredInstruments.length === 0}
            className={`px-4 py-2 text-sm font-medium rounded-lg ${
              bulkToggling || filteredInstruments.length === 0
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-300'
            }`}
          >
            {bulkToggling ? 'Working...' : `Disable All Filtered (${filteredInstruments.length})`}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('id')}>
                  Sr.N {sortConfig.key === 'id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Toggle
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('symbol')}>
                  Symbol {sortConfig.key === 'symbol' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('underlying')}>
                  Underlying {sortConfig.key === 'underlying' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('market_cap')}>
                  Market Cap (Cr) {sortConfig.key === 'market_cap' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('public_private')}>
                  Public/Private {sortConfig.key === 'public_private' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('sector')}>
                  Sector {sortConfig.key === 'sector' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('category')}>
                  Category {sortConfig.key === 'category' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInstruments.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    {instruments.length === 0 
                      ? 'No instruments found. Click "Sync Now" to fetch F&O instruments from NSE.'
                      : 'No instruments match your filters.'
                    }
                  </td>
                </tr>
              ) : (
                filteredInstruments.map((inst, index) => (
                  <tr key={inst.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700">{index + 1}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleInstrument(inst.id)}
                        style={{
                          position: 'relative',
                          display: 'inline-flex',
                          height: '24px',
                          width: '44px',
                          alignItems: 'center',
                          borderRadius: '9999px',
                          backgroundColor: inst.enabled === 1 ? '#22c55e' : '#d1d5db',
                          transition: 'background-color 0.3s ease',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            height: '16px',
                            width: '16px',
                            borderRadius: '9999px',
                            backgroundColor: 'white',
                            transform: inst.enabled === 1 ? 'translateX(24px)' : 'translateX(4px)',
                            transition: 'transform 0.3s ease'
                          }}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{inst.symbol}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{inst.underlying}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">
                      {inst.market_cap === 'N/A' ? (
                        <span className="text-gray-400 italic">N/A</span>
                      ) : (
                        `₹${inst.market_cap}`
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      {editingCell?.id === inst.id && editingCell?.field === 'public_private' ? (
                        <select
                          autoFocus
                          value={inst.public_private || ''}
                          onChange={(e) => handleCellEdit(inst.id, 'public_private', e.target.value)}
                          onBlur={() => setEditingCell(null)}
                          className="px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="PVT">PVT</option>
                          <option value="PUBLIC">PUBLIC</option>
                          <option value="Index">Index</option>
                        </select>
                      ) : (
                        <span
                          onClick={() => setEditingCell({ id: inst.id, field: 'public_private' })}
                          className="cursor-pointer hover:bg-blue-50 px-2 py-1 rounded inline-block min-w-[60px]"
                        >
                          {inst.public_private || <span className="text-gray-400 italic">Click to set</span>}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      {editingCell?.id === inst.id && editingCell?.field === 'sector' ? (
                        <input
                          autoFocus
                          type="text"
                          value={inst.sector || ''}
                          onChange={(e) => {
                            // Update local state immediately as user types
                            setInstruments(prev => prev.map(i => 
                              i.id === inst.id ? { ...i, sector: e.target.value } : i
                            ));
                          }}
                          onBlur={(e) => {
                            // Save to backend when done editing
                            handleCellEdit(inst.id, 'sector', e.target.value);
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleCellEdit(inst.id, 'sector', e.target.value);
                            }
                          }}
                          className="px-2 py-1 border border-blue-500 rounded w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <span
                          onClick={() => setEditingCell({ id: inst.id, field: 'sector' })}
                          className="cursor-pointer hover:bg-blue-50 px-2 py-1 rounded inline-block min-w-[60px]"
                        >
                          {inst.sector || <span className="text-gray-400 italic">Click to set</span>}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      {editingCell?.id === inst.id && editingCell?.field === 'category' ? (
                        <select
                          autoFocus
                          value={inst.category || ''}
                          onChange={(e) => handleCellEdit(inst.id, 'category', e.target.value)}
                          onBlur={() => setEditingCell(null)}
                          className="px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Blank</option>
                          <option value="A">A</option>
                          <option value="B">B</option>
                          <option value="C">C</option>
                          <option value="D">D</option>
                        </select>
                      ) : (
                        <span
                          onClick={() => setEditingCell({ id: inst.id, field: 'category' })}
                          className="cursor-pointer hover:bg-blue-50 px-2 py-1 rounded inline-block min-w-[40px]"
                        >
                          {inst.category || <span className="text-gray-400 italic">-</span>}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Results summary */}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-600">
            Showing {filteredInstruments.length} of {totalInstruments} instruments
          </p>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <div className="flex gap-2 items-start">
          <AlertCircle className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
          <div className="text-sm text-gray-700">
            <p className="font-semibold mb-1">Quick Guide:</p>
            <ul className="list-disc ml-5 space-y-1">
              <li><strong>First time?</strong> Click "Sync Now" to fetch F&O instruments from NSE (takes 1-2 minutes)</li>
              <li><strong>Toggle ON/OFF:</strong> Click switch to enable/disable instruments for trading</li>
              <li><strong>Edit:</strong> Click Public/Private, Sector, or Category cells to edit directly</li>
              <li><strong>MCap Filter:</strong> Enter min/max in crores to filter by market cap (e.g., 100000 = ₹1 lakh crore)</li>
              <li><strong>Bulk Enable/Disable:</strong> Apply filters first, then click to enable/disable all filtered instruments at once</li>
              <li><strong>Upload CSV:</strong> Required: Symbol. Optional: Market Cap, Public/Private, Sector, Category, Enabled</li>
              <li><strong>Download:</strong> Export current data to CSV file (can be re-uploaded after editing)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradableUniverse;